import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { mockMatches } from "@/data/listings";
import NavTop from "@/components/nav/nav_top";
import ButtonGhost from "@/components/buttons/button_ghost";
import { useAuth } from "@/context/AuthContext";

// The Messages inbox: every match conversation in one place, newest
// activity first. Chat is free for every match, so nothing is filtered
// out here. Same GET /matches data as the Matches page.
export default function MessagesPage() {
  const [threads, setThreads] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = Boolean(user?.demo);

  useEffect(() => {
    let alive = true;
    api
      .get("/matches")
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.matches;
        if (alive) setThreads(prep(data?.length ? data : isDemo ? mockMatches : []));
      })
      .catch(() => alive && setThreads(prep(isDemo ? mockMatches : [])));
    return () => { alive = false; };
  }, [isDemo]);

  // The real API uses a last_message OBJECT; the offline demo mocks use a
  // string. Normalize both here. Every match is chattable (chat is free),
  // so no thread is ever filtered out as "locked".
  function prep(list) {
    return list
      .map((m) => ({
        ...m,
        preview: typeof m.last_message === "string" ? m.last_message : m.last_message?.body,
        ts: m.last_message_at || m.last_message?.created_at || m.created_at,
      }))
      .sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
  }

  return (
    <div className="min-h-screen bg-night pb-24 md:pb-10">
      <NavTop />
      <main className="mx-auto max-w-3xl px-4 pt-8 sm:px-6">
        <h1 className="text-2xl font-semibold tracking-tight text-night-text">Messages</h1>
        <p className="mt-1 text-sm text-night-sub">Every conversation from your matches, in one place.</p>

        {!threads ? (
          <div className="mt-16 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
          </div>
        ) : threads.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-night-line bg-night-surface p-10 text-center">
            <MessageCircle className="mx-auto text-night-text" size={26} strokeWidth={1.6} />
            <h2 className="mt-3 text-lg font-semibold text-night-text">No conversations yet</h2>
            <p className="mt-1 text-sm text-night-sub">Match with a home, open the chat and it will show up here.</p>
            <ButtonGhost className="mt-5" onClick={() => navigate("/matches")}>See your matches</ButtonGhost>
          </div>
        ) : (
          <div className="mt-6 divide-y divide-night-line rounded-2xl border border-night-line bg-night-surface">
            {threads.map((m) => (
              <ThreadRow key={m.id} match={m} onOpen={() => navigate(`/chat/${m.id}`)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const ThreadRow = memo(function ThreadRow({ match: m, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-4 p-4 text-left transition hover:bg-night-tint"
    >
      <img
        src={m.listing?.photos?.[0]}
        alt={m.listing?.title}
        loading="lazy"
        decoding="async"
        className="h-14 w-14 shrink-0 rounded-xl object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="truncate text-[15px] font-medium text-night-text">{m.listing?.title}</h3>
          {m.ts && (
            <span className="shrink-0 text-xs text-night-sub">
              {formatDistanceToNow(new Date(m.ts), { addSuffix: true })}
            </span>
          )}
        </div>
        <p className="mt-0.5 line-clamp-1 text-sm text-night-sub">
          {m.preview || `Say hello to ${m.other_party?.name || m.listing?.lister_name || "your match"}`}
        </p>
      </div>
      {m.unread > 0 && (
        <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-brand-primary px-1.5 text-xs font-semibold text-white">
          {m.unread}
        </span>
      )}
    </button>
  );
});
